/**
 * AppSidebar — sidebar shadcn del NeoCore (ADR 014). Porta el nav, el gating por
 * módulo y los badges del AppShell viejo. Dark por defecto (tokens sidebar).
 */
import { useCallback, useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { ChevronRight, ChevronsUpDown, LogOut } from 'lucide-react'
import {
  Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent,
  SidebarGroupLabel, SidebarHeader, SidebarMenu, SidebarMenuBadge,
  SidebarMenuButton, SidebarMenuItem,
} from '@/components/ui/sidebar'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { useAuth } from '@/lib/auth'
import { useRole } from '@/lib/useRole'
import { NAV, type NavItem } from './nav-data'
import { BRAND, IS_DEMO } from '@/lib/brand'

function useBadges() {
  const [counts, setCounts] = useState<Record<string, number>>({})
  const loc = useLocation()
  const refresh = useCallback(async () => {
    const entries = await Promise.all(
      NAV.filter((n) => n.badge).map(async (n) => [n.to, await n.badge!()] as const),
    )
    setCounts(Object.fromEntries(entries))
  }, [])
  useEffect(() => { void refresh() }, [loc.pathname, refresh])
  useEffect(() => { const t = setInterval(() => void refresh(), 30_000); return () => clearInterval(t) }, [refresh])
  return counts
}

function firstNameFromEmail(email: string): string {
  const token = (email.split('@')[0] ?? '').split(/[._-]/)[0] ?? ''
  return token ? token.charAt(0).toUpperCase() + token.slice(1).toLowerCase() : 'Admin'
}

export function AppSidebar() {
  const { session, signOut } = useAuth()
  const { role, canAccess } = useRole()
  const counts = useBadges()
  const loc = useLocation()

  const email = session?.user?.email ?? ''
  const firstName = firstNameFromEmail(email)
  const roleLabel = role === 'admin' ? 'Administrador' : role === 'contador' ? 'Contador' : role === 'backoffice' ? 'Backoffice' : 'Sin rol'

  const visible = NAV.filter((it) => (it.module ? canAccess(it.module) : true))
  const sections: { name: string; items: NavItem[] }[] = []
  for (const it of visible) {
    const name = it.section ?? 'General'
    let s = sections.find((x) => x.name === name)
    if (!s) { s = { name, items: [] }; sections.push(s) }
    s.items.push(it)
  }

  const isActive = (to: string) => {
    if (to === '/' || to === '/neocore/creditos') return loc.pathname === to
    return loc.pathname === to || loc.pathname.startsWith(to + '/')
  }

  // Sidebar minimalista: secciones colapsadas por defecto; se abre sola la que
  // contiene la ruta activa. El operador puede abrir/cerrar las demás a mano.
  const activeSection = sections.find((s) => s.items.some((it) => isActive(it.to)))?.name ?? 'Hoy'
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})
  useEffect(() => {
    setOpenSections((prev) => (prev[activeSection] ? prev : { ...prev, [activeSection]: true }))
  }, [activeSection])
  const sectionOpen = (name: string) => openSections[name] ?? name === activeSection
  const toggleSection = (name: string) =>
    setOpenSections((prev) => ({ ...prev, [name]: !(prev[name] ?? name === activeSection) }))

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild size="lg" className="data-[slot=sidebar-menu-button]:!p-1.5">
              {IS_DEMO ? (
              <NavLink to="/" className="!h-auto">
                {/* Demo: logo completo grande (expandido) / isotipo (colapsado) */}
                <img src={BRAND.logoSobreColor} alt={BRAND.nombre} className="h-14 w-auto max-w-full object-contain py-1 group-data-[collapsible=icon]:hidden" />
                <img src={BRAND.isoOscuro} alt={BRAND.nombre} className="size-7 shrink-0 object-contain hidden group-data-[collapsible=icon]:block" />
              </NavLink>
              ) : (
              <NavLink to="/">
                <img src={BRAND.isoOscuro} alt={BRAND.nombre} className="size-7 shrink-0 object-contain hidden dark:block" />
                <img src={BRAND.isoClaro} alt={BRAND.nombre} className="size-7 shrink-0 object-contain dark:hidden" />
                <div className="grid flex-1 text-left leading-tight">
                  <span className={IS_DEMO ? "truncate font-semibold" : "truncate font-medium lowercase"}>{BRAND.nombre}</span>
                  <span className="truncate text-xs text-muted-foreground">{BRAND.sub}</span>
                </div>
              </NavLink>
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {sections.map((s) => {
          const open = sectionOpen(s.name)
          // Suma de badges de la sección · se muestra junto al título cuando está plegada
          const pendientes = s.items.reduce((acc, it) => acc + (counts[it.to] ?? 0), 0)
          return (
            <SidebarGroup key={s.name}>
              <SidebarGroupLabel asChild>
                <button
                  type="button"
                  onClick={() => toggleSection(s.name)}
                  className="flex w-full items-center gap-1 group/label"
                  aria-expanded={open}
                >
                  <ChevronRight
                    className={`size-3.5 shrink-0 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
                  />
                  <span className="flex-1 text-left">{s.name}</span>
                  {!open && pendientes > 0 ? (
                    <span className="ml-auto rounded-full bg-primary/15 px-1.5 text-[10px] font-semibold text-primary">
                      {pendientes}
                    </span>
                  ) : null}
                </button>
              </SidebarGroupLabel>
              {open ? (
                <SidebarGroupContent>
                  <SidebarMenu>
                    {s.items.map((it) => {
                      const count = counts[it.to]
                      return (
                        <SidebarMenuItem key={it.to}>
                          <SidebarMenuButton asChild isActive={isActive(it.to)} tooltip={it.label}>
                            <NavLink to={it.to} end={it.to === '/' || it.to === '/neocore/creditos'}>
                              <it.Icon />
                              <span>{it.label}</span>
                            </NavLink>
                          </SidebarMenuButton>
                          {count && count > 0 ? (
                            <SidebarMenuBadge className={it.badgeTone === 'warn' ? 'text-primary' : undefined}>{count}</SidebarMenuBadge>
                          ) : null}
                        </SidebarMenuItem>
                      )
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              ) : null}
            </SidebarGroup>
          )
        })}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent">
                  <Avatar className="size-8 rounded-lg">
                    <AvatarFallback className="rounded-lg bg-primary text-primary-foreground">{firstName.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{firstName}</span>
                    <span className="truncate text-xs text-muted-foreground">{roleLabel}</span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="end" className="w-56">
                <DropdownMenuLabel className="truncate text-xs font-normal text-muted-foreground">{email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut}>
                  <LogOut className="size-4" />
                  Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
